/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} PointsDisplayRefs
 * @property {HTMLElement} content
 */

/**
 * Keeps a Floyd Friends points figure in step with the selected variant.
 *
 * Deliberately does no arithmetic, for the same reason member-price.js does none: on a
 * variant change the theme re-renders the section server-side, so the incoming HTML
 * already carries the figure the block's Liquid computed for the new variant. Swapping
 * it in wholesale keeps the displayed points and the configured rate derived from one
 * piece of code.
 *
 * Shared by every block that prints a points figure against a product price. The tag
 * name is read off the element rather than hardcoded, so the matching element in the
 * incoming HTML is found whichever block this is — one implementation, not one per
 * block that would then drift apart.
 *
 * @extends {Component<PointsDisplayRefs>}
 */
class PointsDisplay extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Replaces the rendered content when a different variant is selected.
   * @param {any} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector(`${this.localName}[data-block-id="${this.dataset.blockId}"]`);

        // The new variant may earn nothing, in which case the block renders nothing at
        // all. Removing our own markup is the correct response — leaving a stale figure
        // up would advertise points the order will not award.
        if (!incoming) {
          this.refs.content?.remove();
          return;
        }

        const newContent = incoming.querySelector('[ref="content"]');
        if (!newContent) return;

        if (this.refs.content) {
          this.refs.content.replaceWith(newContent);
        } else {
          this.append(newContent);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn(`[${this.localName}] Event promise rejected:`, error);
      });
  };
}

// One constructor cannot be registered under two tag names, so each block gets a thin
// subclass. There is no behaviour to add — the base class reads the tag name it needs.
class PointsBadge extends PointsDisplay {}
class PointsNudge extends PointsDisplay {}

if (!customElements.get('points-badge')) {
  customElements.define('points-badge', PointsBadge);
}

if (!customElements.get('points-nudge')) {
  customElements.define('points-nudge', PointsNudge);
}
